import { Box, CircularProgress, Typography, makeStyles, Theme } from '@material-ui/core';
import { Refresh } from '@material-ui/icons';
import { Button } from '@roc/ui';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3),
    borderRadius: 8,
  },
  pendingContainer: {
    backgroundColor: '#FDF3D0',
    border: '1px solid #F2DC8C',
  },
  processingContainer: {
    backgroundColor: '#EAF1FD',
    border: '1px solid #BFD3F7',
  },
  badge: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#A0461C',
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '1rem',
  },
  spinner: {
    flex: '0 0 auto',
    color: '#3B6FD8',
  },
  text: {
    flex: '1 1 auto',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  pendingText: {
    color: '#A0461C',
  },
  processingText: {
    color: '#1F4FAF',
  },
  refreshButton: {
    flex: '0 0 auto',
    backgroundColor: '#FFFFFF',
  },
}));

export interface UnclassifiedDocumentsBannerProps {
  pendingCount: number;
  /** At least one document is still being classified by the AI — the table is locked. */
  anyProcessing: boolean;
  onRefresh: () => void;
}

export const UnclassifiedDocumentsBanner = ({
  pendingCount,
  anyProcessing,
  onRefresh,
}: UnclassifiedDocumentsBannerProps) => {
  const classes = useStyles();

  if (anyProcessing) {
    return (
      <Box className={`${classes.container} ${classes.processingContainer}`}>
        <CircularProgress size={28} thickness={5} className={classes.spinner} />
        <Typography className={`${classes.text} ${classes.processingText}`}>
          Some documents are still in progress. The AI is classifying them, so the table is locked
          until it finishes. Refresh to check the latest status.
        </Typography>
        <Button
          className={classes.refreshButton}
          variant="outlined"
          color="primary"
          startIcon={<Refresh />}
          onClick={onRefresh}
          testId="refresh-unclassified-documents"
        >
          Refresh
        </Button>
      </Box>
    );
  }

  return (
    <Box className={`${classes.container} ${classes.pendingContainer}`}>
      <Box className={classes.badge}>{pendingCount}</Box>
      <Typography className={`${classes.text} ${classes.pendingText}`}>
        Documents uploaded through chat could not be classified automatically. Preview each one and
        assign a document type.
      </Typography>
    </Box>
  );
};
